import type { Connection } from '../../../types/electron';
import { lookupConnectionDiagnosticsError } from '../../connection-diagnostics/connectionDiagnosticsErrorMap';
import { recogniseSqlConnectError } from './connectionErrorSqlRecognizer';
import { isCertificateTrustError } from './certificateTrustError';
import { classifyDbError } from '../../../core/analytics/dbErrorClassifier';
import { connectionTargetLine } from './connectionServerLine';
import { mssqlTimeoutHeadlineIn } from './mssqlTimeoutHeadline';

type ConnectionOs = 'macos' | 'windows' | 'linux' | 'unknown';

export type ConnectionErrorPresentation = {
  title: string;
  message: string;
  details: string;
};

type ErrorContext = Pick<Connection, 'engine' | 'server' | 'port'> & {
  /** Structured driver code when it survived IPC (see `Connection` error `errorCode`). */
  errorCode?: string;
  /**
   * `ssh`: the SSH tunnel failed before the database was tried (the connection dialog's result strip, Option C C2). Its
   * message is already a plain sentence from `formatSshTunnelError`, so it is the notice's message as it is.
   */
  origin?: 'ssh';
};

/**
 * Oracle lease gate codes (`electron/oracle-lease/oracleLeaseErrors.ts`) that
 * reach `connect()`'s `errorCode` as the `cause` of the manager's pool-rebuild
 * error. The pool was condemned (a session that never reconciled its close /
 * a close that raced the connect), not the settings — so the hint is "try
 * again", never "check the settings".
 */
const ORACLE_POOL_REBUILD_CODES = new Set(['JAM_ORACLE_POOL_TAINTED', 'JAM_ORACLE_POOL_CLOSED']);
const ORACLE_POOL_REBUILD_MESSAGE = /Oracle connection pool needs to be rebuilt/i;

function detectOs(): ConnectionOs {
  if (typeof navigator === 'undefined') return 'unknown';
  const userAgent = navigator.userAgent || '';

  if (userAgent.includes('Macintosh')) return 'macos';
  if (userAgent.includes('Windows')) return 'windows';
  if (userAgent.includes('Linux')) return 'linux';
  return 'unknown';
}

function normalizeHost(server: string): string {
  return server.split('\\')[0] || server;
}

function extractKnownCode(text: string): string | undefined {
  const lowered = text.toLowerCase();

  if (lowered.includes('connection refused')) return 'ECONNREFUSED';

  const knownCodes = [
    'EPERM',
    'EACCES',
    'EHOSTUNREACH',
    'ENETUNREACH',
    'ECONNREFUSED',
    'ENOTFOUND',
    'ETIMEOUT',
  ] as const;

  return knownCodes.find((code) => text.includes(code));
}

function extractCauseText(details: string): string | undefined {
  const match = details.match(/\(cause:\s*([^)]+)\)/i);
  return match?.[1];
}

function extractTargetHost(details: string, serverFromContext?: string): string | undefined {
  const fromDetails = details.match(
    /Failed to connect to (?<host>\d{1,3}(?:\.\d{1,3}){3}):(?<port>\d{1,5})/i
  )?.groups?.host;
  if (fromDetails) return fromDetails;

  if (serverFromContext) {
    const host = normalizeHost(serverFromContext);
    const ipv4 = host.match(/^(?<host>\d{1,3}(?:\.\d{1,3}){3})$/)?.groups?.host;
    if (ipv4) return ipv4;
  }

  const fromCause = details.match(
    /connect\s+(?<code>E[A-Z0-9]+)\s+(?<host>\d{1,3}(?:\.\d{1,3}){3})\b/i
  )?.groups?.host;
  if (fromCause) return fromCause;

  return undefined;
}

function isPrivateIpv4(host: string): boolean {
  const parts = host.split('.').map((part) => Number(part));
  if (parts.length !== 4 || parts.some((part) => !Number.isFinite(part) || part < 0 || part > 255)) {
    return false;
  }

  const [a, b] = parts;
  if (a === 10) return true;
  if (a === 127) return false;
  if (a === 192 && b === 168) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 169 && b === 254) return true;
  if (a === 100 && b >= 64 && b <= 127) return true;

  return false;
}

export function getConnectionErrorPresentation(
  rawError: string,
  context?: Partial<ErrorContext>
): ConnectionErrorPresentation {
  const details = rawError || 'Unknown connection error';
  const os = detectOs();

  if (context?.origin === 'ssh') {
    return { title: 'SSH tunnel failed', message: details, details };
  }

  // Oracle thin mode refuses servers older than 12.1 outright (NJS-138) —
  // there is no client-side remedy (the app ships thin mode only), so the
  // value here is telling the user plainly instead of leaving them to retry.
  if (/\bNJS-138\b/i.test(details)) {
    return {
      title: 'Oracle server version not supported',
      message:
        "This Oracle server version isn't supported by the bundled driver — it requires Oracle Database 12.1 or newer.",
      details,
    };
  }

  if (
    (context?.errorCode && ORACLE_POOL_REBUILD_CODES.has(context.errorCode.toUpperCase())) ||
    ORACLE_POOL_REBUILD_MESSAGE.test(details)
  ) {
    return {
      title: 'Connection pool rebuilt',
      message: 'The connection pool had to be rebuilt — try connecting again.',
      details,
    };
  }

  const causeText = extractCauseText(details);
  const code = extractKnownCode(causeText || details);

  const targetHost = extractTargetHost(details, context?.server);
  const targetIsPrivate = targetHost ? isPrivateIpv4(targetHost) : false;

  // TLS certificate trust failure — most commonly a self-signed certificate
  // on a dev/internal server. Checked before the OS-code branches: tedious
  // wraps these as ESOCKET-style errors whose cause text would otherwise fall
  // through to generic copy.
  if (isCertificateTrustError(details)) {
    const hasTrustCheckbox = context?.engine === 'mssql' || context?.engine === 'mysql';
    return {
      title: 'Server certificate not trusted',
      message: hasTrustCheckbox
        ? 'The server presented a TLS certificate this machine doesn\'t trust — commonly a self-signed certificate. If you trust this server, enable "Trust server certificate" in the connection settings and try again.'
        : "The server presented a TLS certificate this machine doesn't trust — commonly a self-signed or internal certificate. Review the SSL settings for this connection, or install the server's CA certificate on this machine.",
      details,
    };
  }

  const isPermissionDeniedCode = code === 'EPERM' || code === 'EACCES';
  const isNetworkUnreachableCode = code === 'EHOSTUNREACH' || code === 'ENETUNREACH';

  const isMacLocalNetworkPermissionLikely =
    os === 'macos' && (isPermissionDeniedCode || (isNetworkUnreachableCode && targetIsPrivate));

  if (isMacLocalNetworkPermissionLikely) {
    return {
      title: 'Local network access blocked',
      message:
        "macOS may be blocking local network connections for this app. Open System Settings → Privacy & Security → Local Network and make sure Jam SQL Studio is enabled. If it is already enabled, try toggling it off and back on — macOS sometimes requires this to refresh the permission.",
      details,
    };
  }

  if (isPermissionDeniedCode) {
    if (os === 'windows') {
      return {
        title: 'Network access blocked',
        message:
          'Windows blocked this network connection. Check Windows Defender Firewall / antivirus rules for Jam SQL Studio, then try connecting again.',
        details,
      };
    }

    if (os === 'linux') {
      return {
        title: 'Network access blocked',
        message:
          'This connection was blocked by the OS. Check firewall settings (ufw/iptables) or sandbox permissions (Flatpak/Snap), then try connecting again.',
        details,
      };
    }

    return {
      title: 'Network access blocked',
      message: 'This connection was blocked by the OS. Fix the permission issue, then try connecting again.',
      details,
    };
  }

  if (code === 'ECONNREFUSED') {
    return {
      title: 'Connection refused',
      message:
        'The server refused the connection. Check the host and port, and that the database server is running and accepts connections, then try again.',
      details,
    };
  }

  if (code === 'ENOTFOUND') {
    return {
      title: 'Server not found',
      message: 'The server hostname could not be resolved. Check the address/DNS, then try connecting again.',
      details,
    };
  }

  // Any engine's connect timeout (a driver's ETIMEOUT / ETIMEDOUT, SQL Tools Service's "Connection timeout after 30
  // seconds"): named after the endpoint it dialed, in user terms — the raw text, which can name internal components,
  // stays in the details (Windows release pass B3). A SQL Server timeout whose message main led with its own headline
  // (`mssqlTimeoutHeadline.ts`: the target's likely cause, e.g. a local Developer / Express server with TCP/IP off)
  // shows that headline (review round 2); every other engine composes none and keeps the generic line.
  if (code === 'ETIMEOUT' || classifyDbError(context?.engine, details, context?.errorCode).category === 'timeout') {
    const target = context?.server ? connectionTargetLine({ server: context.server, port: context.port, engine: context.engine }) : '';
    const generic = target
      ? `${target} did not answer in time. Check that the server is running and reachable from this computer — the address, the port, a firewall or VPN — then try again.`
      : 'The connection timed out. Check network/VPN/firewall rules, then try connecting again.';
    return { title: 'Connection timed out', message: mssqlTimeoutHeadlineIn(details) ?? generic, details };
  }

  if (isNetworkUnreachableCode) {
    return {
      title: 'Network unreachable',
      message: "The server could not be reached. Verify network routing/VPN, then try connecting again.",
      details,
    };
  }

  // SQL Server-level error recognition. Connect-time IPC stringifies
  // `error.message`, dropping the structured `RequestError.number` extracted
  // in the main process — so we phrase-match the raw text against the SQL
  // Server error vocabulary and reuse the diagnostics error map for
  // consistent wording across connect-time and listing-time failures.
  const sqlError = recogniseSqlConnectError(details);
  if (sqlError.sqlErrorCode !== undefined || sqlError.driverCode) {
    const mapped = lookupConnectionDiagnosticsError({
      sqlErrorCode: sqlError.sqlErrorCode,
      code: sqlError.driverCode,
    });
    if (mapped.category !== 'other') {
      const title =
        mapped.category === 'database_missing'
          ? 'Database not found'
          : mapped.category === 'firewall'
            ? 'Firewall blocked'
            : mapped.category === 'permission'
              ? 'Permission denied'
              : 'Login rejected';
      return { title, message: mapped.description, details };
    }
  }

  return {
    title: 'Connection failed',
    message: 'Jam SQL Studio could not connect. Check the connection settings and try again.',
    details,
  };
}
